import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge,
} from "react-bootstrap";
import { fetchRoiSettings, bulkUpdateRoiSettings } from "./DataService";

const UNITS = {
  roi_driver_rate:     { unit: "€/h",      desc: "Coût total employeur : salaire brut + charges patronales (~42%)" },
  roi_fuel_price:      { unit: "€/L",       desc: "Prix à la pompe du carburant (gazole ou essence)" },
  roi_consumption:     { unit: "L/100 km",  desc: "Consommation moyenne d'un utilitaire chargé" },
  roi_vehicle_monthly: { unit: "€/mois",    desc: "Leasing ou amortissement + assurance tous risques du véhicule" },
};

function ParametresDigestRoi() {
  const [settings, setSettings] = useState([]);
  const [values, setValues]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetchRoiSettings()
      .then((data) => {
        const items = data.settings?.roi ?? [];
        setSettings(items);
        const initial = {};
        items.forEach((s) => { initial[s.id] = String(s.value); });
        setValues(initial);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger les paramètres.");
        setLoading(false);
      });
  }, []);

  const handleChange = (id, val) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      const payload = Object.entries(values).map(([id, value]) => ({
        id: parseInt(id, 10),
        value: parseFloat(value),
      }));
      await bulkUpdateRoiSettings(payload);
      setSuccess(true);
    } catch {
      setError("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // Calcul prévisualisation €/km
  const fuelSetting      = settings.find((s) => s.key === "roi_fuel_price");
  const consumptionSetting = settings.find((s) => s.key === "roi_consumption");
  const fuelPrice        = fuelSetting      ? parseFloat(values[fuelSetting.id]      || 0) : 0;
  const consumption      = consumptionSetting ? parseFloat(values[consumptionSetting.id] || 0) : 0;
  const fuelPerKm        = consumption > 0 ? ((fuelPrice * consumption) / 100 + 0.06).toFixed(3) : "—";

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col>
          <h2>Paramètres — Digest ROI Clients</h2>
          <p className="text-muted">
            Ces valeurs servent à estimer le coût d'un livreur interne dans le rapport ROI mensuel
            envoyé à <code>contact@marion-materiaux.fr</code>.
            Les changements s'appliquent au prochain envoi automatique (2 du mois).
          </p>
        </Col>
      </Row>

      {error   && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Paramètres enregistrés avec succès.</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <strong>🚐 Coût d'un livreur interne estimé</strong>
          </Card.Header>
          <Card.Body>
            {settings.map((s) => {
              const meta = UNITS[s.key] ?? { unit: "", desc: "" };
              return (
                <Form.Group as={Row} className="mb-3 align-items-start" key={s.id}>
                  <Form.Label column sm={5}>
                    <div>{s.label}</div>
                    {meta.desc && (
                      <small className="text-muted">{meta.desc}</small>
                    )}
                  </Form.Label>
                  <Col sm={3}>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={values[s.id] ?? ""}
                      onChange={(e) => handleChange(s.id, e.target.value)}
                      required
                    />
                  </Col>
                  <Col sm={2} className="text-muted d-flex align-items-center">
                    {meta.unit}
                  </Col>
                </Form.Group>
              );
            })}
          </Card.Body>
        </Card>

        {/* Prévisualisation */}
        <Card className="mb-4 border-info">
          <Card.Header className="bg-info bg-opacity-10">
            <strong>📊 Prévisualisation du calcul</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col sm={6}>
                <p className="mb-1">
                  <strong>Coût carburant + entretien par km :</strong>{" "}
                  <Badge bg="secondary">{fuelPerKm} €/km</Badge>
                </p>
                <small className="text-muted">
                  = ({fuelPrice} €/L × {consumption} L/100km) / 100 + 0,06 €/km entretien
                </small>
              </Col>
              <Col sm={6}>
                <p className="mb-1 text-muted" style={{ fontSize: "0.85em" }}>
                  Exemple pour une livraison de 20 km A/R, 45 min réelles :
                </p>
                <ul className="mb-0" style={{ fontSize: "0.85em" }}>
                  <li>
                    🧑 Chauffeur : {((parseFloat(values[settings.find(s=>s.key==="roi_driver_rate")?.id])||0) * 0.75).toFixed(2)} €
                    <span className="text-muted"> (45 min)</span>
                  </li>
                  <li>
                    ⛽ Carburant : {(parseFloat(fuelPerKm||0) * 20).toFixed(2)} €
                    <span className="text-muted"> (20 km)</span>
                  </li>
                  <li>
                    🚐 Véhicule : proratisé sur nb livraisons du client
                  </li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-end mb-5">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Enregistrer
          </Button>
        </div>
      </Form>
    </Container>
  );
}

export default ParametresDigestRoi;
